/**
 * CR121: Preview Debug Panel for Theme Labs
 * 
 * Displays diagnostics to help debug preview failures:
 * - Current preview mode (Single/All Types)
 * - Token source (Live from draft vs Saved)
 * - Resolved token snapshot
 * - Recovery actions (Retry, Reset)
 */

import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  Bug,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  RotateCcw,
  Copy,
  Check,
  AlertTriangle,
  Eye,
  Code,
} from "lucide-react";
import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";
import { toast } from "sonner";

// ================================================================
// TYPES
// ================================================================

interface PreviewDebugPanelProps {
  /** Current preview mode */
  previewMode: "single" | "multi";
  /** Selected deal type (for single mode) */
  selectedDealType?: string;
  /** Whether preview is using draft or saved tokens */
  tokenSource: "draft" | "saved";
  /** Current draft tokens */
  draftTokens: GlobalThemeTokens;
  /** Saved tokens for comparison */
  savedTokens: GlobalThemeTokens;
  /** Whether there are unsaved changes */
  hasChanges: boolean;
  /** Last action performed */
  lastAction?: string;
  /** Error message if preview failed */
  previewError?: string | null;
  /** Callback to retry preview */
  onRetry: () => void;
  /** Callback to reset draft to saved */
  onResetToSaved: () => void;
  /** Optional additional debug info */
  debugInfo?: Record<string, unknown>;
}

// ================================================================
// COMPONENT
// ================================================================

export function PreviewDebugPanel({
  previewMode,
  selectedDealType,
  tokenSource,
  draftTokens,
  savedTokens,
  hasChanges,
  lastAction,
  previewError,
  onRetry,
  onResetToSaved,
  debugInfo,
}: PreviewDebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showFullTokens, setShowFullTokens] = useState(false);
  const [copied, setCopied] = useState(false);

  // Build resolved token snapshot (top-level fields only for compact display)
  const resolvedSnapshot = {
    "typography.bodyFontId": draftTokens.typography.bodyFontId ?? "inter",
    "typography.headingFontId": draftTokens.typography.headingFontId ?? "inter",
    "typography.fontScale": draftTokens.typography.fontScale,
    "colors.accentColor": draftTokens.colors.accentColor,
    "colors.cardBackground": draftTokens.colors.cardBackground,
    "components.cardCornerRadius": `${draftTokens.components.cardCornerRadius}px`,
    "components.cardShadow": draftTokens.components.cardShadow,
    "components.cardBorderWidth": `${draftTokens.components.cardBorderWidth}px`,
    "spacing.headerHeight": draftTokens.spacing.headerHeight,
  };

  // Copy debug JSON to clipboard
  const handleCopyDebug = useCallback(async () => {
    const debugPayload = {
      timestamp: new Date().toISOString(),
      previewMode,
      selectedDealType,
      tokenSource,
      hasChanges,
      lastAction,
      previewError,
      resolvedSnapshot,
      fullDraftTokens: draftTokens,
      fullSavedTokens: savedTokens,
      debugInfo,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(debugPayload, null, 2));
      setCopied(true);
      toast.success("Debug JSON copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error("Failed to copy to clipboard");
    }
  }, [previewMode, selectedDealType, tokenSource, hasChanges, lastAction, previewError, resolvedSnapshot, draftTokens, savedTokens, debugInfo]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
        >
          <Bug className="h-3 w-3" />
          Preview Debug
          {previewError && <AlertTriangle className="h-3 w-3 text-destructive" />}
          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <Card className="border-dashed">
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-xs flex items-center gap-2">
              <Code className="h-3 w-3" />
              Preview Diagnostics
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-3 space-y-3">
            {/* Error Alert */}
            {previewError && (
              <Alert variant="destructive" className="py-2">
                <AlertTriangle className="h-3 w-3" />
                <AlertDescription className="text-xs ml-2">
                  <strong>Preview Error:</strong> {previewError}
                </AlertDescription>
              </Alert>
            )}

            {/* Status Grid */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="space-y-1">
                <span className="text-muted-foreground">Mode:</span>
                <Badge variant="outline" className="ml-2 text-[10px]">
                  {previewMode === "single" ? "Single" : "All Types"}
                </Badge>
              </div>
              {previewMode === "single" && selectedDealType && (
                <div className="space-y-1">
                  <span className="text-muted-foreground">Deal Type:</span>
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    {selectedDealType}
                  </Badge>
                </div>
              )}
              <div className="space-y-1">
                <span className="text-muted-foreground">Token Source:</span>
                <Badge 
                  variant={tokenSource === "draft" ? "default" : "secondary"} 
                  className="ml-2 text-[10px]"
                >
                  <Eye className="h-2.5 w-2.5 mr-1" />
                  {tokenSource === "draft" ? "Live from draft" : "Saved tokens"}
                </Badge>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground">Unsaved:</span>
                <Badge 
                  variant={hasChanges ? "secondary" : "outline"} 
                  className="ml-2 text-[10px]"
                >
                  {hasChanges ? "Yes" : "No"}
                </Badge>
              </div>
            </div>

            {/* Last Action */}
            {lastAction && (
              <div className="text-xs">
                <span className="text-muted-foreground">Last Action:</span>
                <span className="ml-2 font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded">
                  {lastAction}
                </span>
              </div>
            )}

            {/* CR208: Enhanced debug info - draftHash and renderTick */}
            {debugInfo && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {debugInfo.draftHash && (
                  <div className="space-y-1">
                    <span className="text-muted-foreground">Draft Hash:</span>
                    <Badge variant="outline" className="ml-2 text-[10px] font-mono">
                      {String(debugInfo.draftHash).slice(0, 20)}
                    </Badge>
                  </div>
                )}
                {debugInfo.renderTick !== undefined && (
                  <div className="space-y-1">
                    <span className="text-muted-foreground">Render Tick:</span>
                    <Badge variant="outline" className="ml-2 text-[10px] font-mono">
                      #{String(debugInfo.renderTick)}
                    </Badge>
                  </div>
                )}
                {debugInfo.previewKey && (
                  <div className="col-span-2 space-y-1">
                    <span className="text-muted-foreground">Preview Key:</span>
                    <span className="ml-2 font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded break-all">
                      {String(debugInfo.previewKey).slice(0, 50)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* Resolved Tokens Snapshot */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Resolved Tokens</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 text-[10px] px-1.5"
                  onClick={() => setShowFullTokens(!showFullTokens)}
                >
                  {showFullTokens ? "Compact" : "Expand"}
                </Button>
              </div>
              <div className="bg-muted/50 rounded p-2 text-[10px] font-mono max-h-32 overflow-y-auto">
                {showFullTokens ? (
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(draftTokens, null, 2)}
                  </pre>
                ) : (
                  <div className="space-y-0.5">
                    {Object.entries(resolvedSnapshot).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground">{key}:</span>
                        <span>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Recovery Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1 flex-1"
                onClick={onRetry}
              >
                <RefreshCw className="h-3 w-3" />
                Retry Preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1 flex-1"
                onClick={onResetToSaved}
                disabled={!hasChanges}
              >
                <RotateCcw className="h-3 w-3" />
                Reset to Saved
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={handleCopyDebug}
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}
